import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import logo from "../../Assets/Images/landing/eww-logo.png";
import "../../Assets/Styles/pages/homeRedesign.scss";
import { usePremiumStatus } from "../../hooks/usePremiumStatus";
import PremiumGateDialog from "../Common/PremiumGateDialog";

interface AppSidebarProps {
  active: "home" | "templates" | "batch";
  isLoggedIn: boolean;
  setOpenSignIn: (value: boolean) => void;
  // Opt-in per page (currently just /addtemplate) rather than global, so /home's sidebar
  // stays exactly as-is.
  showPremiumUpsell?: boolean;
  /** Swallows every navigation click while a page-level operation is mid-flight (e.g. a batch
   * upload) that leaving would corrupt. Deliberately *silent* -- no disabled styling or
   * cursor change, per design: clicks simply don't respond. */
  disableNavigation?: boolean;
}

/**
 * Left nav rail shared by /home, /addtemplate, and /batch-watermark (extracted from
 * home.tsx's original inline markup so all pages stay visually/behaviorally identical
 * instead of drifting apart).
 */
const AppSidebar: React.FC<AppSidebarProps> = ({
  active,
  isLoggedIn,
  setOpenSignIn,
  showPremiumUpsell,
  disableNavigation,
}) => {
  const history = useHistory();
  const isPremium = usePremiumStatus();
  const [gateDialogOpen, setGateDialogOpen] = useState(false);

  const handleTemplatesClick = () => {
    if (disableNavigation) return;
    if (isLoggedIn) history.push("/addtemplate");
    else setOpenSignIn(true);
  };

  const handleBatchClick = () => {
    if (disableNavigation) return;
    if (isLoggedIn) history.push("/batch-watermark");
    else setOpenSignIn(true);
  };

  return (
    <div className="home-sidebar">
      <div
        className="home-sidebar__logo"
        onClick={() => {
          if (disableNavigation) return;
          history.push("/");
        }}
      >
        <img src={logo} alt="eZy Watermark" />
        <span>eZy Watermark</span>
      </div>

      <div
        className={`home-sidebar__nav-item ${active === "home" ? "active" : ""}`}
        onClick={
          active === "home" || disableNavigation
            ? undefined
            : () => history.push("/home")
        }
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M4 11.5 12 4l8 7.5" stroke="#6b7280" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M6 10v9a1 1 0 0 0 1 1h3v-5h4v5h3a1 1 0 0 0 1-1v-9" stroke="#6b7280" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Home
      </div>

      <div
        className={`home-sidebar__nav-item ${active === "templates" ? "active" : ""}`}
        onClick={handleTemplatesClick}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="8" height="8" rx="1.5" stroke="#6b7280" strokeWidth="1.6" />
          <rect x="13" y="3" width="8" height="8" rx="1.5" stroke="#6b7280" strokeWidth="1.6" />
          <rect x="3" y="13" width="8" height="8" rx="1.5" stroke="#6b7280" strokeWidth="1.6" />
          <rect x="13" y="13" width="8" height="8" rx="1.5" stroke="#6b7280" strokeWidth="1.6" />
        </svg>
        Templates
      </div>

      <div
        className={`home-sidebar__nav-item ${active === "batch" ? "active" : ""}`}
        onClick={handleBatchClick}
      >
        <span className="image-icon-mask image-icon-mask--grey" />
        Batch Watermark
      </div>

      {showPremiumUpsell && isLoggedIn && isPremium === false && (
        <div className="home-sidebar__upsell">
          <div className="home-sidebar__upsell-title">
            <span aria-hidden="true">👑</span> Go Premium
          </div>
          <p className="home-sidebar__upsell-subtitle">
            Unlock all premium features and templates.
          </p>
          <button
            type="button"
            className="home-sidebar__upsell-btn"
            onClick={() => setGateDialogOpen(true)}
          >
            Upgrade Now <span aria-hidden="true">&rarr;</span>
          </button>
        </div>
      )}

      <PremiumGateDialog
        open={gateDialogOpen}
        onClose={() => setGateDialogOpen(false)}
        title="Upgrade to Pro"
      />
    </div>
  );
};

export default AppSidebar;
